import { Capacitor, registerPlugin } from "@capacitor/core";
import { detectWebRuntimeCapabilities } from "./platform-capabilities.ts";

type MidiFilePlugin = {
  save(options: { base64: string; fileName: string; mimeType: string }): Promise<{ uri: string; fileName: string }>;
  open(): Promise<{ base64: string; fileName: string; uri: string }>;
};

export type MidiSaveResult = { fileName: string; destination: "android-document" | "web-download" | "web-share"; uri?: string };
export type MidiOpenResult = { fileName: string; bytes: Uint8Array; source: "android-document" | "web-picker"; uri?: string };

const NativeMidiFile = registerPlugin<MidiFilePlugin>("MidiFile");

export function normalizeMidiFileName(fileName: string): string {
  const safe = fileName.trim().replaceAll("\\", "_").replaceAll("/", "_").replace(/[:*?"<>|]+/g, "_") || "multitrack-vocal-midi";
  return safe.toLowerCase().endsWith(".mid") || safe.toLowerCase().endsWith(".midi") ? safe : safe + ".mid";
}

export function midiBytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export function midiBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64.replace(/\s+/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function webDownload(bytes: Uint8Array, fileName: string): void {
  const payload = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(payload).set(bytes);
  const blob = new Blob([payload], { type: "audio/midi" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function shareMidiFile(bytes: Uint8Array, fileName: string): Promise<void> {
  const payload = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(payload).set(bytes);
  const file = new File([payload], fileName, { type: "audio/midi" });
  if (!navigator.canShare?.({ files: [file] })) throw new Error("この端末ではMIDIファイル共有を利用できません");
  await navigator.share(midiFileSharePayload(file));
}

export function midiFileSharePayload(file: File): ShareData {
  // iOS Files turns Web Share text/title fields into a second .txt attachment.
  // A MIDI export is a file-only operation, so keep the payload intentionally minimal.
  return { files: [file] };
}

function webOpen(): Promise<MidiOpenResult | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".mid,.midi,audio/midi,audio/x-midi";
    input.hidden = true;
    const finish = () => input.remove();
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) {
        finish();
        resolve(null);
        return;
      }
      file.arrayBuffer()
        .then((buffer) => {
          finish();
          resolve({ fileName: file.name, bytes: new Uint8Array(buffer), source: "web-picker" });
        })
        .catch((error) => {
          finish();
          reject(error);
        });
    }, { once: true });
    input.addEventListener("cancel", () => {
      finish();
      resolve(null);
    }, { once: true });
    document.body.append(input);
    input.click();
  });
}

export async function openMidiFile(): Promise<MidiOpenResult | null> {
  if (Capacitor.getPlatform() === "android" && Capacitor.isPluginAvailable("MidiFile")) {
    try {
      const result = await NativeMidiFile.open();
      return {
        fileName: result.fileName || "import.mid",
        bytes: midiBase64ToBytes(result.base64),
        source: "android-document",
        uri: result.uri,
      };
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      if (/cancel/i.test(message) || /キャンセル/.test(message)) return null;
      throw caught;
    }
  }
  return webOpen();
}

export async function saveMidiFile(bytes: Uint8Array, requestedName: string): Promise<MidiSaveResult> {
  const fileName = normalizeMidiFileName(requestedName);
  if (Capacitor.getPlatform() === "android" && Capacitor.isPluginAvailable("MidiFile")) {
    const result = await NativeMidiFile.save({ base64: midiBytesToBase64(bytes), fileName, mimeType: "audio/midi" });
    return { fileName: result.fileName || fileName, destination: "android-document", uri: result.uri };
  }
  const capabilities = detectWebRuntimeCapabilities();
  if (capabilities.appleMobile && capabilities.fileShare) {
    await shareMidiFile(bytes, fileName);
    return { fileName, destination: "web-share" };
  }
  webDownload(bytes, fileName);
  return { fileName, destination: "web-download" };
}
